/**
 * FT-458 — Soft-delete tombstone helper (dependency-free leaf).
 *
 * ── Why this file exists ──────────────────────────────────────────────────
 * ARRAY_COLLECTIONS sync via a cloud∪local UNION keyed by `id` (see the push
 * merge in UniversalSyncBridge.js + outbox.js, and the pull delta-merge
 * `mergePullDelta`). The union has NO set-difference: an item that is simply
 * ABSENT from the local array is re-seeded from the cloud copy and pushed
 * straight back. So a UI delete that DROPS the item from the array is
 * structurally impossible to propagate — the delete "comes back".
 *
 * `sr.maintenance_jobs.v1` avoided this with the FT-182 archive tombstone
 * (`status:'archived'` kept IN the array); `sr.crew.v1` with the FT-175
 * `deletedAt` (camelCase, epoch ms) tombstone. FT-458 gives the remaining
 * safety collections (defects, drills, incidents, maintenance_history) the same
 * treatment via a canonical `deleted_at` (snake_case, ISO string) marker.
 *
 * `isTombstoned` recognises ALL THREE existing shapes so ONE filter covers
 * every collection — no migration of crew / maintenance_jobs required:
 *   - FT-458 snake  `deleted_at`  (ISO string)  ← new collections
 *   - FT-175 camel  `deletedAt`   (epoch ms)     ← crew (unchanged)
 *   - FT-182 status `'archived'`                 ← maintenance_jobs (unchanged)
 *
 * This is a dependency-free leaf (pure functions, no React / db / bridge
 * import) so the bridge push merge, the pull delta-merge AND the outbox drain
 * merge can all import it without recreating the
 * `UniversalSyncBridge → outbox → UniversalSyncBridge` cycle that FT-331
 * deliberately broke.
 *
 * RETAIN-FOREVER (v1): a tombstone is NEVER hard-destroyed. It is kept in the
 * persisted + pushed array (so the deletion propagates to cloud + other
 * devices) and the UI simply FILTERS it out everywhere it renders / counts.
 * Server-side GC is a deferred follow-up ticket (see design report) — do NOT
 * add GC here: a premature GC can race a slow device's pull and resurrect.
 *
 * @module sync/tombstone
 */

/**
 * Is this item soft-deleted (tombstoned)?
 *
 * Tolerates all three shipped tombstone shapes (see module header) so a single
 * filter covers new + legacy collections. A `deletedAt`/`deleted_at` value of
 * `null`/`undefined`/`0`/'' is NOT a tombstone (that is the "restored" / never
 * deleted state — see FT-175 restore which sets `deletedAt: null`).
 *
 * @param {unknown} item
 * @returns {boolean}
 */
export const isTombstoned = (item) =>
  !!(
    item &&
    typeof item === 'object' &&
    (item.deleted_at || item.deletedAt || item.status === 'archived')
  );

/**
 * Filter out tombstoned items — used at every UI read / count site so a
 * deleted record never renders and never inflates a compliance / attention
 * count. The PERSISTED array must keep the tombstones: do NOT use this before
 * writing to storage / sync — only for display / derivation.
 *
 * @param {Array<Object>} items
 * @returns {Array<Object>} items with tombstones removed
 */
export const filterTombstoned = (items) =>
  (Array.isArray(items) ? items : []).filter((i) => !isTombstoned(i));

/**
 * FT-458 sticky-retain at merge sites.
 *
 * Given the LOCAL copy of an item and the payload that the timestamp tie-break
 * (resolvePull/PushMergeWinner) would otherwise persist, return the payload with
 * the LOCAL tombstone markers re-applied when local is tombstoned but the
 * winning payload is NOT. This makes a deletion STICKY: a stale non-tombstoned
 * cloud copy can never un-delete a locally-deleted record, and the deletion
 * still propagates (the merged payload carries the tombstone forward).
 *
 * Rides the EXISTING FT-296/FT-446 tie-break winners — it does NOT reintroduce a
 * `!localTs || localTs >= cloudTs` gate. It only ever ADDS the tombstone marker
 * onto the already-chosen winner; it never changes which record body wins.
 *
 * Marker preservation is shape-aware so we never invent a shape:
 *   - snake  `deleted_at` / `deleted_by`  (FT-458 new)
 *   - camel  `deletedAt`  / `deletedBy`   (FT-175 crew)
 *   - status `'archived'` + `archivedAt`  (FT-182 maintenance_jobs)
 *
 * If the winner is ALREADY tombstoned (e.g. both sides deleted, or cloud is the
 * fresher tombstone) the winner is returned unchanged.
 *
 * @param {Object} localItem  the local copy (may be tombstoned)
 * @param {Object} winner     the payload the tie-break chose to persist
 * @returns {Object} winner, with local tombstone markers re-applied if needed
 */
export const retainTombstone = (localItem, winner) => {
  // Nothing to preserve if local isn't tombstoned, or the winner already is.
  if (!isTombstoned(localItem) || isTombstoned(winner)) return winner;

  const preserved = { ...winner };
  if (localItem.deleted_at) {
    preserved.deleted_at = localItem.deleted_at;
    if (localItem.deleted_by !== undefined) preserved.deleted_by = localItem.deleted_by;
  }
  if (localItem.deletedAt) {
    preserved.deletedAt = localItem.deletedAt;
    if (localItem.deletedBy !== undefined) preserved.deletedBy = localItem.deletedBy;
  }
  if (localItem.status === 'archived') {
    preserved.status = 'archived';
    if (localItem.archivedAt !== undefined) preserved.archivedAt = localItem.archivedAt;
  }
  return preserved;
};

const tombstone = { isTombstoned, filterTombstoned, retainTombstone };
export default tombstone;
